/**
 * Los hooks de datos.
 *
 * Envuelven a `api`; **nunca contienen una consulta ni una URL armada a mano**.
 * Un componente que necesita datos llama a un hook de acá, y si el endpoint
 * cambia se cambia en un solo lugar.
 */

import {
  useMutation, useQuery, useQueryClient,
  type UseMutationOptions, type UseQueryOptions,
} from '@tanstack/react-query';
import { api, ApiError } from './api';

/** Una consulta GET tipada. La clave es la ruta: única y estable por definición. */
export function useApi<T>(
  path: string | null,
  options?: Omit<UseQueryOptions<T, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<T, ApiError>({
    queryKey: [path],
    queryFn: () => api.get<T>(path!),
    enabled: path !== null,
    ...options,
  });
}

/**
 * Una mutación que invalida lo que corresponda al terminar.
 *
 * `invalidates` recibe prefijos de ruta: tras confirmar un pago se invalida
 * `/admin/receivables` y `/admin/dashboard` de una vez, y las pantallas abiertas
 * se refrescan solas. Olvidarlo es cómo un panel termina mostrando un saldo que
 * ya no existe.
 */
export function useApiMutation<TData, TVars = void>(
  mutationFn: (vars: TVars) => Promise<TData>,
  invalidates: string[] = [],
  options?: Omit<UseMutationOptions<TData, ApiError, TVars>, 'mutationFn'>,
) {
  const client = useQueryClient();
  return useMutation<TData, ApiError, TVars>({
    mutationFn,
    ...options,
    onSuccess: async (...args) => {
      await Promise.all(invalidates.map((prefix) => client.invalidateQueries({
        predicate: (q) => typeof q.queryKey[0] === 'string' && q.queryKey[0].startsWith(prefix),
      })));
      await options?.onSuccess?.(...args);
    },
  });
}
